import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { type Entry, type Frise as FriseData, fetchFrise, fetchSettings, queryKeys } from "./api";
import { AppPage } from "./app-nav";
import { todayIso } from "./dates";
import { EntrySheet } from "./entry-form";
import { Frise } from "./frise";
import { datedInOrder, undatedEntries } from "./frise-layout";
import { MemberFrisePage } from "./member-view";
import { SessionGate } from "./session-gate";
import { UndatedTray } from "./undated-tray";

/**
 * L'entree `/` (spec, section 1, ecrans 1 et 2) : sans session l'ecran de connexion, avec session
 * la frise. La session se lit par la frise elle-meme : `GET /api/t/frise` repond 401 sans session.
 */
export function HomePage({ today = todayIso() }: { today?: string } = {}) {
  const frise = useQuery({ queryKey: queryKeys.frise, queryFn: fetchFrise, retry: false });
  if (frise.isPending) {
    return (
      <p className="text-muted-foreground" role="status">
        Chargement...
      </p>
    );
  }
  if (frise.isError) return <SessionGate error={frise.error} retry={() => void frise.refetch()} />;
  // Le role vient du serveur, qui a deja filtre les points : l'ecran ne fait que suivre.
  if (frise.data.role !== "owner") return <MemberFrisePage frise={frise.data} today={today} />;
  return <FrisePage frise={frise.data} today={today} />;
}

/** L'ecran du point ouvert : nouveau, modification, ou « Dater » (focus sur « Quand ? »). */
type Editing = { entry: Entry | null; focusWhen: boolean } | null;

function FrisePage({ frise, today }: { frise: FriseData; today: string }) {
  const settings = useQuery({ queryKey: queryKeys.settings, queryFn: fetchSettings, retry: false });
  const [editing, setEditing] = useState<Editing>(null);
  const birthDate = settings.data?.birthDate ?? null;
  const dated = datedInOrder(frise.entries);
  const undated = undatedEntries(frise.entries);
  const usedTracks = new Set(frise.entries.map((entry) => entry.trackId)).size;
  const openNew = () => setEditing({ entry: null, focusWhen: false });

  return (
    <AppPage>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="tl-display m-0 text-[32px] md:text-[44px]">
            Ma vie, <em className="text-tl-accent italic">en étoiles</em>
          </h1>
          <p className="m-0 mt-2 text-sm text-muted-foreground">
            {momentsLabel(frise.entries.length)} sur {pistesLabel(usedTracks)}
            {frise.undatedCount > 0 ? (
              <>
                {" · "}
                <a className="text-muted-foreground underline" href="#a-dater">
                  {frise.undatedCount} à dater
                </a>
              </>
            ) : null}
          </p>
        </div>
        <button
          className="fixed right-4 bottom-[76px] z-20 inline-flex size-[54px] items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-semibold text-primary-foreground shadow-[0_0_24px_rgba(242,193,78,0.35)] md:static md:size-auto md:min-h-11 md:px-[18px]"
          onClick={openNew}
          type="button"
        >
          <svg
            aria-hidden="true"
            fill="none"
            height="18"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2.4"
            viewBox="0 0 24 24"
            width="18"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span className="sr-only md:not-sr-only">Ajouter un moment</span>
        </button>
      </div>

      {dated.length > 0 ? (
        <Frise
          birthDate={birthDate}
          entries={frise.entries}
          onEdit={(entry) => setEditing({ entry, focusWhen: false })}
          today={today}
          tracks={frise.tracks}
        />
      ) : (
        <div className="flex flex-col items-start gap-2 rounded-xl border border-border bg-card p-5">
          <p className="tl-display m-0 text-2xl">Ta frise est encore vide.</p>
          <p className="m-0 text-sm text-muted-foreground">
            Commence par un moment dont tu te souviens bien : une naissance, un premier jour, un
            voyage.
          </p>
        </div>
      )}

      <UndatedTray
        entries={undated}
        onDate={(entry) => setEditing({ entry, focusWhen: true })}
        tracks={frise.tracks}
      />

      {editing !== null ? (
        <EntrySheet
          entry={editing.entry}
          focusWhen={editing.focusWhen}
          onClose={() => setEditing(null)}
          tracks={frise.tracks}
        />
      ) : null}
    </AppPage>
  );
}

const momentsLabel = (count: number) =>
  count <= 1 ? `${String(count)} moment` : `${String(count)} moments`;
const pistesLabel = (count: number) =>
  count <= 1 ? `${String(count)} piste` : `${String(count)} pistes`;
